import { useEffect, useRef } from 'react';

import type { JobNotification } from '@cartwhl/common/stores/jobNotificationStore';
import Loading from '@cartwhl/ui/atoms/Loading';
import Text from '@cartwhl/ui/base/TextNew';
import CheckmarkIcon from '@cartwhl/ui/icons/CheckmarkIcon';
import CircleCrossIcon from '@cartwhl/ui/icons/CircleCrossIcon';

import { NotificationListItemLayout } from './NotificationListItem';

export default function JobNotificationListItem({
  notification,
  scrollRoot,
  onViewed,
}: {
  notification: JobNotification;
  scrollRoot: HTMLElement | null;
  onViewed: (batchID: string, event: JobNotification['event']) => void;
}) {
  const itemRef = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (notification.status === 'read' || !itemRef.current) return;
    if (typeof IntersectionObserver === 'undefined') {
      onViewed(notification.batchID, notification.event);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries.some(
            (entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5,
          )
        ) {
          onViewed(notification.batchID, notification.event);
          observer.disconnect();
        }
      },
      { root: scrollRoot, threshold: 0.5 },
    );
    observer.observe(itemRef.current);
    return () => observer.disconnect();
  }, [
    notification.batchID,
    notification.status,
    notification.event,
    onViewed,
    scrollRoot,
  ]);

  const icon =
    notification.event === 'started' ? (
      <Loading className="text-secondary size-5" />
    ) : notification.event === 'failed' ? (
      <CircleCrossIcon className="text-red size-5" />
    ) : (
      <CheckmarkIcon
        className={`${notification.status === 'new' ? 'text-green' : 'text-tertiary'} size-5`}
      />
    );
  return (
    <NotificationListItemLayout
      notification={{ ...notification, notificationID: notification.batchID }}
      itemRef={itemRef}
      icon={
        <span
          role="img"
          aria-label={
            notification.event === 'started'
              ? 'Capturing motion'
              : notification.event === 'failed'
                ? 'Capture failed'
                : 'Capture complete'
          }
        >
          {icon}
        </span>
      }
    >
      <Text token="14/medium" className="text-primary truncate">
        {notification.title}
      </Text>
      <Text token="14" className="text-tertiary truncate">
        {notification.event === 'failed'
          ? 'Performance capture job · Failed'
          : 'Performance capture job'}
      </Text>
    </NotificationListItemLayout>
  );
}
